import { IsVarEmpty } from '../utils/GlobalFunctions'

/**
 * This class is the base class for all Javascript based dials.
 * It will have the following default styling:
 * Backgound Colour: Akixi Blue (#005497)
 * Foreground Colour: White
 * Reverse Gauge Face: False
 * Reverse Gauge Direction: False
 *
 * @param {object} pNode The DOM element to attach the dial canvas to.
 */
abstract class CDialBase {
    // Constants.
    protected readonly iANIMATION_PERIOD_MS = 500
    protected readonly iUPDATE_PERIOD_MS = 30 // Roughly 30fps - only used in IE9.
    // protected member variables.
    protected m_iTimer = 0
    protected m_iAnimationCallbackID = 0
    protected readonly m_bUseAnimationFrames = !IsVarEmpty(window.requestAnimationFrame)
    // Public member variables.
    protected m_pNode: HTMLElement
    protected m_flCurrentValue = 0
    protected m_pCanvas: HTMLCanvasElement
    protected m_sBackgroundColour = '#005497'
    protected m_sForegroundColour = '#FFFFFF'
    protected m_sFontFamily = 'Muli, sans serif, arial'
    // Colour components of the foreground colour.
    protected m_iForegroundRed = 255
    protected m_iForegroundGreen = 255
    protected m_iForegroundBlue = 255
    protected m_flMinValue = 0
    protected m_flMaxValue = 1.0
    protected m_flRange = this.m_flMaxValue - this.m_flMinValue
    protected m_bReverseDirection = false
    protected m_bReverseGuageFace = false
    protected m_sDisplayValue = ''
    protected m_iWidth = 0
    protected m_iHeight = 0
    protected m_iDialType: number
    protected abstract Draw(flValueNew: number, flValueOld: number, flFraction: number): void

    public constructor(pNode: HTMLElement, iDialType: number) {
        this.m_pNode = pNode
        this.m_iDialType = iDialType
        // Creates the canvas element and attaches it to the DOM.
        this.m_pCanvas = document.createElement('canvas')
        this.m_pNode.appendChild(this.m_pCanvas)
    }

    /**
     * Public method.
     * Deletes all attached resources.
     */
    public DeleteResources(): void {
        if (this.m_iTimer !== 0) {
            clearInterval(this.m_iTimer)
            this.m_iTimer = 0
        }
        if (this.m_iAnimationCallbackID !== 0) {
            window.cancelAnimationFrame(this.m_iAnimationCallbackID)
            this.m_iAnimationCallbackID = 0
        }
        //
        this.m_pNode = document.documentElement
        this.m_flCurrentValue = 0
        this.m_sBackgroundColour = '#005497'
        this.m_sForegroundColour = '#FFFFFF'
        this.m_sFontFamily = ''
        this.m_iForegroundRed = 0
        this.m_iForegroundGreen = 0
        this.m_iForegroundBlue = 0
        this.m_flMinValue = 0
        this.m_flMaxValue = 0
        this.m_flRange = 0
        this.m_bReverseDirection = false
        this.m_bReverseGuageFace = false
        this.m_sDisplayValue = ''
        this.m_iWidth = 0
        this.m_iHeight = 0
        this.m_iDialType = 0
        //
    }

    /**
     * protected method.
     *
     * Update the internal RGB fields based upon a colour hex string.
     *
     * @param {string} sHexString The hexidecimal value as a string value.
     */
    protected HexStringToRGB(sHexString: string): void {
        const sRegexPattern = /#([A-Fa-f0-9]{2})([A-Fa-f0-9]{2})([A-Fa-f0-9]{2})/
        const pMatch = sRegexPattern.exec(sHexString)
        if (pMatch !== null) {
            this.m_iForegroundRed = parseInt(pMatch[1], 16)
            this.m_iForegroundGreen = parseInt(pMatch[2], 16)
            this.m_iForegroundBlue = parseInt(pMatch[3], 16)
        }

        if (isNaN(this.m_iForegroundRed)) {
            this.m_iForegroundRed = 255
        }

        if (isNaN(this.m_iForegroundGreen)) {
            this.m_iForegroundGreen = 255
        }

        if (isNaN(this.m_iForegroundBlue)) {
            this.m_iForegroundBlue = 255
        }
    }

    protected BuildForegroundWithOpacity(flOpacity: number): string {
        return `rgba(${this.m_iForegroundRed},${this.m_iForegroundGreen},${this.m_iForegroundBlue},${flOpacity})`
    }

    /**
     * Public method.
     *
     * Returns the dial type associated with this dial.
     *
     * @returns {integer} The current dial type.
     */
    public getDialType(): number {
        return this.m_iDialType
    }

    /**
     * Public method.
     *
     * Sets the fomt family of the dial.
     */
    public setFontFamily(sFontFamily: string): void {
        this.m_sFontFamily = sFontFamily
    }

    /**
     * Set the zero value of the dial.
     * @param {number} flValue The new mimumum value.
     */
    public setMinimumValue(flValue: number): void {
        this.m_flMinValue = flValue
        this.m_flRange = this.m_flMaxValue - this.m_flMinValue
    }

    /**
     * Set the full-scale value of the dial.
     * @param {number} flValue The new maximum value.
     */
    public setMaximumValue(flValue: number): void {
        this.m_flMaxValue = flValue
        this.m_flRange = this.m_flMaxValue - this.m_flMinValue
    }

    /**
     * Sets the display value for the dial
     *
     * @param {string} sDisplayValue The current value to display.
     */
    public setDisplayValue(sDisplayValue: string): void {
        this.m_sDisplayValue = sDisplayValue
    }
    /**
     * Public method.
     *
     * @param {string} sColour Colour for the lines and text.
     */
    public setForegroundColour(sColour: string): void {
        this.m_sForegroundColour = sColour
        this.HexStringToRGB(sColour)
    }

    /**
     * Public method.
     *
     * @param {string} sColour Colour for the background of the dial.
     */
    public setBackgroundColour(sColour: string): void {
        this.m_sBackgroundColour = sColour
    }

    /**
     * Reattach the canvas to a new DOM element.
     * @param {object} The new DOM node to attach to.
     */
    public Reattach(pNode: HTMLElement): void {
        this.m_pNode = pNode
        this.m_pNode.appendChild(this.m_pCanvas)
    }

    /**
     * Public method.
     *
     * @param {number} flValueNew The new value for the dial, it will smooothly
     * transition between the new and old values.
     */
    public setValue(flValueNew: number): void {
        if (this.m_iWidth > 0 && this.m_iHeight > 0) {
            const flValueOld = this.m_flCurrentValue
            let flFraction = 0
            let iStart = 0

            if (this.m_iTimer !== 0) {
                clearInterval(this.m_iTimer)
                this.m_iTimer = 0
            }
            if (this.m_iAnimationCallbackID !== 0) {
                window.cancelAnimationFrame(this.m_iAnimationCallbackID)
                this.m_iAnimationCallbackID = 0
            }

            this.m_flCurrentValue = flValueNew
            const pUpdateFunction = (iTimestamp: number): void => {
                if (this.m_bUseAnimationFrames) {
                    if (iStart === 0) {
                        iStart = iTimestamp
                    }
                    flFraction = (iTimestamp - iStart) / this.iANIMATION_PERIOD_MS
                } else {
                    flFraction += this.iUPDATE_PERIOD_MS / this.iANIMATION_PERIOD_MS
                }

                if (flFraction > 1) {
                    flFraction = 1
                }

                this.Draw(flValueNew, flValueOld, flFraction)

                if (flFraction >= 1) {
                    if (!this.m_bUseAnimationFrames) {
                        clearInterval(this.m_iTimer)
                        this.m_iTimer = 0
                    }
                } else if (this.m_bUseAnimationFrames) {
                    this.m_iAnimationCallbackID = window.requestAnimationFrame(pUpdateFunction)
                }
            }

            if (this.m_bUseAnimationFrames) {
                this.m_iAnimationCallbackID = window.requestAnimationFrame(pUpdateFunction)
            } else {
                this.m_iTimer = setInterval(pUpdateFunction, this.iUPDATE_PERIOD_MS)
            }
        }
    }

    /**
     * Public method to set the size of the canvase
     */
    public setSize(iWidth: number, iHeight: number): void {
        if (this.m_iWidth !== iWidth || this.m_iHeight !== iHeight) {
            this.m_iWidth = iWidth
            this.m_iHeight = iHeight
            this.m_pCanvas.width = this.m_iWidth
            this.m_pCanvas.height = this.m_iHeight
        }
    }

    /**
     * Public Method.
     *
     * Reverses the dial face.
     *
     * @param {boolean} bReverse Whether to reverse the gauge face.
     */
    public setReverseGaugeFace(bReverse: boolean): void {
        this.m_bReverseGuageFace = bReverse
    }

    /**
     * Public Method.
     *
     * Reverses the guage direction.
     *
     * @param {boolean} bReverse Whether to reverse the gauge direction.
     */
    public setReverseGaugeDirection(bReverse: boolean): void {
        this.m_bReverseDirection = bReverse
    }
}

export default CDialBase
